"use client";

import { CloseIcon } from "icons";
import React from "react";
import { Modal, ModalOverlay, Dialog, Button } from "react-aria-components";
import { Spinner } from "ui-components";

import {
  useOrganizationSharedFileGetNewsletterArchiveById,
  getOrganizationSharedFileGetNewsletterArchiveByIdQueryKey,
} from "@/src/hooks/api/generated";
import { formateDateFromUTC } from "@/utils/formateDate";

interface INewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
  newsletterId: number;
}

export default function NewsletterModal({
  isOpen,
  onClose,
  newsletterId,
}: INewsletterModalProps): JSX.Element {
  const { data: newsletter, status } =
    useOrganizationSharedFileGetNewsletterArchiveById(newsletterId, {
      query: {
        queryKey:
          getOrganizationSharedFileGetNewsletterArchiveByIdQueryKey(
            newsletterId
          ),
        enabled: isOpen && !!newsletterId,
      },
    });

  const handleDownloadPdf = (): void => {
    if (!newsletter?.pdfUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = newsletter.pdfUrl;
    link.download = "";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      isDismissable
      className={({ isEntering, isExiting }) => `
        fixed inset-0 z-50 overflow-y-auto bg-black/60 flex items-start justify-center pt-[env(safe-area-inset-top)] sm:pt-10 sm:p-4 text-center pb-0
        ${isEntering ? "animate-in fade-in duration-300 ease-out" : ""}
        ${isExiting ? "animate-out fade-out duration-200 ease-in" : ""}
      `}
    >
      <Modal
        className={({ isEntering, isExiting }) => `
          w-full sm:max-w-[1000px] bg-white text-left align-middle shadow-xl overflow-hidden min-h-full sm:min-h-0 sm:rounded-2xl relative
          ${isEntering ? "animate-in zoom-in-95 ease-out duration-300" : ""}
          ${isExiting ? "animate-out zoom-out-95 ease-in duration-200" : ""}
        `}
      >
        <Dialog
          role="dialog"
          className="flex flex-col h-full sm:h-[80vh] outline-none"
        >
          <div className="flex justify-between items-start px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10 shrink-0">
            <div className="flex flex-col gap-1 pr-4 min-w-0">
              <h2 className="text-[18px] font-semibold">
                {newsletter?.name || "Loading..."}
              </h2>
              {newsletter?.publishedAt ? (
                <div className="text-[14px] text-gray-500">
                  {formateDateFromUTC(newsletter.publishedAt, "MMMM D, YYYY")}
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {newsletter?.pdfUrl ? (
                <Button
                  onPress={handleDownloadPdf}
                  className="py-2 px-6 rounded-full inline-flex justify-center items-center whitespace-nowrap hover:shadow-lg bg-primary hover:text-black text-sm font-medium border-none cursor-pointer outline-none"
                >
                  Download PDF
                </Button>
              ) : null}
              <Button
                onPress={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition outline-none shrink-0 border-none bg-transparent cursor-pointer"
              >
                <CloseIcon className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50">
            {status === "pending" ? (
              <div className="flex flex-1 items-center justify-center h-full min-h-[400px]">
                <Spinner intent="brand-primary" />
              </div>
            ) : newsletter?.content ? (
              <div
                className="w-full bg-white rounded-lg sm:rounded shadow-sm overflow-hidden"
                dangerouslySetInnerHTML={{ __html: newsletter.content }}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center h-full min-h-[400px] text-gray-500">
                No content available for this newsletter.
              </div>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
